<script lang="ts" setup>
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import RowItem, { RowItemProps } from '@/molecules/RowItem.vue';
import TitleHeader from '@/molecules/TitleHeader.vue';
import RowItemContainer from '@/organisms/RowItemContainer.vue';
import LocalIcon from '@/atoms/LocalIcon.vue';
import BaseLabel from '@/atoms/BaseLabel.vue';
import { useAppModal } from '@/composables/useAppModal';
import { flowStore } from '@/parcours/stores/flowStore';
import {
  BodyPartKey,
  getRiskInfo,
  tmsBodyPartsConfig,
} from '@/parcours/flows/tms/tmsBodyParts.config';

const emit = defineEmits(['back', 'next', 'submit', 'canSubmit']);

const props = defineProps<{ bodyPart: BodyPartKey; submitAttempted?: boolean }>();

const route = useRoute();
const router = useRouter();
const modal = useAppModal();

const config = computed(() => tmsBodyPartsConfig[props.bodyPart]);
const risk = computed(() => getRiskInfo(flowStore.data?.[config.value.riskField]));
const answeredQuestionsCount = computed(
  () =>
    config.value.questions.filter((q) => {
      const value = flowStore.data?.[q.key];
      return value !== undefined && value !== null;
    }).length,
);

function goToQuestions() {
  router.push({
    name: `flow-${route.meta.flowId}-questionsDetail`,
    params: route.params,
    query: { symptom: props.bodyPart },
  });
}

function goToSyndrome(field: string) {
  router.push({
    name: `flow-${route.meta.flowId}-syndromeQuestion`,
    params: route.params,
    query: { symptom: props.bodyPart, syndrome: field },
  });
}

async function openRecommendation() {
  await modal.openModal({
    title: config.value.recommendation.title,
    content: config.value.recommendation.content,
  });
}

function syndromeDescription(field: string): string {
  const value = flowStore.data?.[field];
  switch (value) {
    case 'PROVEN':
      return 'Oui';
    case 'NONEXISTENT':
      return 'Non';
    case 'LATENT':
      return 'Oui (forme latente)';
    case 'SYMPTOMATIC':
      return 'Oui (forme symptomatique)';
    default:
      return 'Non renseigné';
  }
}

function isSyndromeAnswered(field: string): boolean {
  const value = flowStore.data?.[field];
  return value !== undefined && value !== null && value !== '';
}

const isComplete = computed(() =>
  config.value.syndromes.every((syndrome) => isSyndromeAnswered(syndrome.field)),
);

watch(isComplete, (value) => emit('canSubmit', value), { immediate: true });

const syndromeRowItems = computed<RowItemProps[]>(() =>
  config.value.syndromes.map((syndrome) => {
    const missing = props.submitAttempted && !isSyndromeAnswered(syndrome.field);
    return {
      id: syndrome.field,
      title: syndrome.label,
      description: syndromeDescription(syndrome.field),
      descriptionProps: missing ? { color: 'error' as const } : undefined,
      class: 'py-4',
      onClick: () => goToSyndrome(syndrome.field),
    };
  }),
);
</script>

<template>
  <div>
    <TitleHeader
      :description="`Risque ${risk.label}`"
      :description-color="risk.tone"
      :on-back="() => router.back()"
      :title="config.label"
    />

    <RowItem
      id="questions"
      class="border rounded-xl mt-4"
      :description="`${answeredQuestionsCount}/${config.questions.length} questions répondues`"
      :icon="{ name: 'user', background: 'brand', size: '32' }"
      title="Questions"
      @click="goToQuestions"
    >
      <LocalIcon name="chevron-right" />
    </RowItem>

    <div v-if="syndromeRowItems.length" class="mt-4">
      <RowItemContainer :row-items="syndromeRowItems" row-item-type="RowItem">
        <LocalIcon name="chevron-right" />
      </RowItemContainer>

      <BaseLabel
        v-if="submitAttempted && !isComplete"
        class="mt-2 px-4"
        color="error"
        preset="small"
        text="Merci de répondre à toutes les questions avant de continuer."
      />
    </div>

    <RowItem
      id="recommendation"
      class="border rounded-xl mt-4"
      :icon="{ name: 'info', background: 'brand', size: '32' }"
      title="Recommandations"
      @click="openRecommendation"
    >
      <LocalIcon name="chevron-right" />
    </RowItem>
  </div>
</template>
